/**
 * TypingDots -- Realtime primitive for streaming surfaces.
 *
 * Renders three animated dots indicating that content is loading or
 * streaming. Consumed by RefinementRun, BriefAnalyzer, EmailThread,
 * and WorkspaceConversations.
 *
 * The staggered animation delays are 0ms / 160ms / 320ms (via
 * --motion-stagger from tokens.css). The voiceTypingDot keyframe
 * from motion.css drives the bounce. Under prefers-reduced-motion:
 * reduce, users see static dots (the global fallback in motion.css
 * collapses animation-duration to 0.01ms).
 *
 * Authored by T304 (FR-010).
 *
 * @see apps/dashboard/src/styles/motion.css
 */
import React from "react";
import type { CSSProperties } from "react";
import { useT } from "../../i18n/useT";

export interface TypingDotsProps {
  /** Accessible label for the animation. Default: t("component.loading-more") */
  "aria-label"?: string;
}

const DOT_SIZE = 6;
const DOT_GAP = 4;

const containerStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: `${DOT_GAP}px`,
  padding: "var(--space-xs) var(--space-sm)",
};

const dotBaseStyle: CSSProperties = {
  display: "inline-block",
  width: `${DOT_SIZE}px`,
  height: `${DOT_SIZE}px`,
  borderRadius: "var(--radius-full)",
  background: "var(--fg3, #75758a)",
};

/**
 * Three dots with staggered voiceTypingDot animation.
 * Delays: 0ms, 160ms (4 * 40ms), 320ms (8 * 40ms).
 * The stagger interval comes from --motion-stagger (40ms).
 */
export function TypingDots(props: TypingDotsProps) {
  const t = useT();

  const label = props["aria-label"] ?? t("component.loading-more");

  return (
    <span
      role="status"
      aria-label={label}
      data-testid="typing-dots"
      style={containerStyle}
    >
      <span
        className="motion__voice-typing-dot"
        style={{ ...dotBaseStyle, animationDelay: "0ms" }}
      />
      <span
        className="motion__voice-typing-dot"
        style={{ ...dotBaseStyle, animationDelay: "160ms" }}
      />
      <span
        className="motion__voice-typing-dot"
        style={{ ...dotBaseStyle, animationDelay: "320ms" }}
      />
    </span>
  );
}

export default TypingDots;
